import * as React from "react";
import { useState, useEffect, useMemo } from "react";
import { ColumnFilter, ColumnSort, RiskScoreBadge, TD, TH } from "security-app/components/common";
import { useFetchAssetsFilterOptions } from "security-app/hooks/useAssetsAPI";
import { usePagination, useSetPagination } from "common/contexts";
import { useColumnTogglerContext } from "security-app/components/common/ColumnToggler";
const columnsWithFilter = ["usernames", "user_ids", "email_addresses", "first_name", "last_name"];
const getInitColumns = (showDetails) => ({
  usernames: { visible: showDetails, group: "User" },
  user_id: { visible: showDetails, group: "User" },
  email: { visible: showDetails, group: "User" },
  user_risk_score: { visible: showDetails, group: "User" },
  first_name: { visible: showDetails, group: "User" },
  last_name: { visible: showDetails, group: "User" }
});
export function UserHeaders({ showDetails }) {
  var _a, _b, _c, _d, _e, _f;
  const pagination = usePagination();
  const setPagination = useSetPagination();
  const [filterOptions, setFilterOptions] = useState({ id: null });
  const { cacheId, columns, columnsDispatch } = useColumnTogglerContext();
  useEffect(() => {
    if (showDetails) {
      columnsDispatch({ type: "init", payload: { cacheId, initState: getInitColumns(showDetails) } });
    } else {
      columnsDispatch({ type: "reset", payload: { cacheId, colsToRemove: Object.keys(getInitColumns(showDetails)) } });
    }
  }, [cacheId, columnsDispatch, showDetails]);
  const { fetchAssetsFilterOptions, fetchingAssetsFilterOptions } = useFetchAssetsFilterOptions();
  useMemo(async () => {
    const userCols = Object.keys(columns).filter((col) => columns[col].group === "User").filter((value) => columnsWithFilter.includes(value));
    const filters = await fetchAssetsFilterOptions({ fields: userCols });
    setFilterOptions(filters);
  }, [columns, fetchAssetsFilterOptions]);
  const usernameOptions = useMemo(() => {
    if (!(filterOptions == null ? void 0 : filterOptions.usernames)) return [];
    const options = [];
    filterOptions.usernames.forEach((username) => {
      options.push({ label: username.toString(), value: username.toString(), type: "text" });
    });
    return options;
  }, [filterOptions]);
  const userIdOptions = useMemo(() => {
    if (!(filterOptions == null ? void 0 : filterOptions.user_ids)) return [];
    const options = [];
    filterOptions.user_ids.forEach((user_id) => {
      options.push({ label: user_id.toString(), value: user_id.toString(), type: "text" });
    });
    return options;
  }, [filterOptions]);
  const emailOptions = useMemo(() => {
    if (!(filterOptions == null ? void 0 : filterOptions.email_addresses)) return [];
    const options = [];
    filterOptions.email_addresses.forEach((email) => {
      options.push({ label: email.toString(), value: email.toString(), type: "text" });
    });
    return options;
  }, [filterOptions]);
  const firstNameOptions = useMemo(() => {
    if (!(filterOptions == null ? void 0 : filterOptions.first_name)) return [];
    const options = [];
    filterOptions.first_name.forEach((first_name) => {
      options.push({ label: first_name.toString(), value: first_name.toString(), type: "text" });
    });
    return options;
  }, [filterOptions]);
  const lastNameOptions = useMemo(() => {
    if (!(filterOptions == null ? void 0 : filterOptions.last_name)) return [];
    const options = [];
    filterOptions.last_name.forEach((last_name) => {
      options.push({ label: last_name.toString(), value: last_name.toString(), type: "text" });
    });
    return options;
  }, [filterOptions]);
  const applyFilters = (filterKey, filterValues) => {
    const auxFilters = { ...pagination.filters, [filterKey]: filterValues };
    if (!filterValues || filterValues.length === 0) delete auxFilters[filterKey];
    setPagination({ ...pagination, filters: auxFilters });
  };
  const applySort = (field, direction) => {
    setPagination({ ...pagination, orderBy: field, direction });
  };
  return /* @__PURE__ */ React.createElement(React.Fragment, null, !fetchingAssetsFilterOptions && /* @__PURE__ */ React.createElement(React.Fragment, null, ((_a = columns.usernames) == null ? void 0 : _a.visible) && /* @__PURE__ */ React.createElement(TH, null, /* @__PURE__ */ React.createElement(
    ColumnSort,
    {
      field: "usernames",
      orderBy: pagination.orderBy,
      direction: pagination.direction,
      onSort: applySort
    },
    /* @__PURE__ */ React.createElement(
      ColumnFilter,
      {
        filterBy: "usernames",
        type: "text",
        filterData: usernameOptions,
        appliedFilters: pagination.filters,
        onClose: applyFilters
      },
      "Usernames"
    )
  )), ((_b = columns.user_id) == null ? void 0 : _b.visible) && /* @__PURE__ */ React.createElement(TH, null, /* @__PURE__ */ React.createElement(
    ColumnSort,
    {
      field: "user_ids",
      orderBy: pagination.orderBy,
      direction: pagination.direction,
      onSort: applySort
    },
    /* @__PURE__ */ React.createElement(
      ColumnFilter,
      {
        filterBy: "user_ids",
        type: "text",
        filterData: userIdOptions,
        appliedFilters: pagination.filters,
        onClose: applyFilters
      },
      "User IDs"
    )
  )), ((_c = columns.email) == null ? void 0 : _c.visible) && /* @__PURE__ */ React.createElement(TH, null, /* @__PURE__ */ React.createElement(
    ColumnFilter,
    {
      filterBy: "email_addresses",
      type: "text",
      filterData: emailOptions,
      appliedFilters: pagination.filters,
      onClose: applyFilters
    },
    "Email Addresses"
  )), ((_d = columns.user_risk_score) == null ? void 0 : _d.visible) && /* @__PURE__ */ React.createElement(TH, null, /* @__PURE__ */ React.createElement(
    ColumnSort,
    {
      field: "user_risk_score",
      orderBy: pagination.orderBy,
      direction: pagination.direction,
      onSort: applySort
    },
    "Risk Score"
  )), ((_e = columns.first_name) == null ? void 0 : _e.visible) && /* @__PURE__ */ React.createElement(TH, null, /* @__PURE__ */ React.createElement(
    ColumnSort,
    {
      field: "first_name",
      orderBy: pagination.orderBy,
      direction: pagination.direction,
      onSort: applySort
    },
    /* @__PURE__ */ React.createElement(
      ColumnFilter,
      {
        filterBy: "first_name",
        type: "text",
        filterData: firstNameOptions,
        appliedFilters: pagination.filters,
        onClose: applyFilters
      },
      "First Name"
    )
  )), ((_f = columns.last_name) == null ? void 0 : _f.visible) && /* @__PURE__ */ React.createElement(TH, null, /* @__PURE__ */ React.createElement(
    ColumnSort,
    {
      field: "last_name",
      orderBy: pagination.orderBy,
      direction: pagination.direction,
      onSort: applySort
    },
    /* @__PURE__ */ React.createElement(
      ColumnFilter,
      {
        filterBy: "last_name",
        type: "text",
        filterData: lastNameOptions,
        appliedFilters: pagination.filters,
        onClose: applyFilters
      },
      "Last Name"
    )
  ))));
}
export function UserData({ assetDetails, showDetails }) {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i;
  const { columns } = useColumnTogglerContext();
  return showDetails && /* @__PURE__ */ React.createElement(React.Fragment, null, ((_a = columns.usernames) == null ? void 0 : _a.visible) && /* @__PURE__ */ React.createElement(TD, null, ((_b = assetDetails.usernames) == null ? void 0 : _b.join(", ")) || ""), ((_c = columns.user_id) == null ? void 0 : _c.visible) && /* @__PURE__ */ React.createElement(TD, null, ((_d = assetDetails.user_ids) == null ? void 0 : _d.join(", ")) || ""), ((_e = columns.email) == null ? void 0 : _e.visible) && /* @__PURE__ */ React.createElement(TD, null, ((_f = assetDetails.email_addresses) == null ? void 0 : _f.join(", ")) || ""), ((_g = columns.user_risk_score) == null ? void 0 : _g.visible) && /* @__PURE__ */ React.createElement(TD, null, /* @__PURE__ */ React.createElement(RiskScoreBadge, { riskScore: assetDetails.risk_score_normalized })), ((_h = columns.first_name) == null ? void 0 : _h.visible) && /* @__PURE__ */ React.createElement(TD, null, assetDetails.first_name || ""), ((_i = columns.last_name) == null ? void 0 : _i.visible) && /* @__PURE__ */ React.createElement(TD, null, assetDetails.last_name || ""));
}
